import AlertCategoryEnum from '../enums/AlertCategoryEnum';
import AlertOriginEnum from '../enums/AlertOriginEnum';
import AlertSeverityEnum from '../enums/AlertSeverityEnum';
import AlertStateEnum from '../enums/AlertStateEnum';
import HealthParameterEnum from '../enums/HealthParameterEnum';

/**
 * Rótulos e cores dos dois eixos do alerta.
 *
 * ⚠️ **Gravidade e categoria têm mapas separados porque são colunas separadas**
 * (AJ-01). Um mapa único, de "tipo de alerta" para cor, é exatamente o badge que a
 * spec mandou desfazer — ele obrigaria a escolher um dos eixos para exibir.
 *
 * As cores vêm de `02-alertas.md`: Grave vermelho, Moderado laranja, Engajamento
 * verde. As duas primeiras são de gravidade e a última de categoria — o bloco de
 * contadores mistura os eixos, e a tela reproduz isso em vez de "corrigir".
 */
const getAlertLabels = {
  severity: (severity: AlertSeverityEnum): string =>
    severity === AlertSeverityEnum.SEVERE ? 'Grave' : 'Moderado',

  severityColor: (severity: AlertSeverityEnum): string =>
    severity === AlertSeverityEnum.SEVERE ? '#EA7676' : '#FA8822',

  category: (category: AlertCategoryEnum): string => {
    switch (category) {
      case AlertCategoryEnum.BODY_SIGNS:
        return 'Sinais do Corpo';
      case AlertCategoryEnum.LOW_SCORE:
        return 'Scores baixos';
      case AlertCategoryEnum.ENGAGEMENT:
        return 'Engajamento';
      default:
        return 'Não identificado';
    }
  },

  categoryColor: (category: AlertCategoryEnum): string => {
    switch (category) {
      case AlertCategoryEnum.ENGAGEMENT:
        return '#6FCF97';
      default:
        return '#828282';
    }
  },

  /**
   * ⚠️ **"Relógio (não confirmado)" e não só "Relógio".** A §1.2 de `02-alertas.md`
   * manda a fila mostrar que a leitura do relógio ainda **não foi verificada** — o
   * sensor óptico de pulso tem margem de erro clínica desconhecida, e parte destes
   * alertas será falso positivo. Omitir a ressalva daria à leitura do relógio a
   * mesma autoridade de uma aferição por foto de tensiômetro.
   */
  origin: (origin: AlertOriginEnum | null): string => {
    switch (origin) {
      case AlertOriginEnum.WATCH:
        return 'Relógio (não confirmado)';
      case AlertOriginEnum.PHOTO:
        return 'Foto do visor';
      case AlertOriginEnum.SELF_REPORTED:
        return 'Informado pela pessoa';
      case AlertOriginEnum.SYMPTOM:
        return 'Sintoma relatado';
      // Alerta de varredura (silêncio, score) não tem procedência, e o traço é mais
      // honesto que inventar uma.
      default:
        return '—';
    }
  },

  /**
   * O nome do parâmetro — o rótulo do que disparou o alerta, na coluna "Parâmetro".
   *
   * ⓘ **"Frequência cardíaca", não "FC em repouso"** — decisão de 24/08: o relógio
   * não sabe se a pessoa estava em repouso. A chave interna ainda é
   * `resting_heart_rate` porque renomeá-la arrasta o tipo do Postgres, e isso anda
   * junto com a decisão de faixa que segue aberta (tarefa 4.13).
   */
  parameter: (parameter: string): string => {
    switch (parameter) {
      case HealthParameterEnum.BLOOD_PRESSURE:
        return 'Pressão arterial';
      case HealthParameterEnum.RESTING_HEART_RATE:
        return 'Frequência cardíaca';
      case HealthParameterEnum.BLOOD_OXYGEN:
        return 'Saturação (SpO₂)';
      case HealthParameterEnum.BODY_TEMPERATURE:
        return 'Temperatura';
      case HealthParameterEnum.SLEEP:
        return 'Sono';
      case HealthParameterEnum.GLUCOSE:
        return 'Glicemia';
      case HealthParameterEnum.WEIGHT_LOSS:
        return 'Perda de peso';
      case 'device_silence':
        return 'Silêncio do dispositivo';
      default:
        return parameter || '—';
    }
  },

  state: (state: AlertStateEnum): string => {
    switch (state) {
      case AlertStateEnum.IN_QUEUE:
        return 'Na fila';
      case AlertStateEnum.IN_SERVICE:
        return 'Em atendimento';
      case AlertStateEnum.TREATED:
        return 'Tratado';
      // A spec dá o texto: o prontuário mostra "Resolvido automaticamente pela
      // CintIA" para distinguir do que a enfermagem tratou.
      case AlertStateEnum.SELF_RESOLVED:
        return 'Resolvido pela CintIA';
      case AlertStateEnum.IN_PRE_TRIAGE:
        return 'Em pré-triagem';
      case AlertStateEnum.ESCALATED:
        return 'Escalado';
      // "Gravado sem fila" e não "Registrado" seco: o rótulo tem de dizer por que o
      // caso não está na fila de ninguém, senão parece alerta perdido.
      case AlertStateEnum.RECORDED:
        return 'Gravado sem fila';
      default:
        return 'Detectado';
    }
  },
};

export default getAlertLabels;
